import { EfficiencyModel } from '../../types';
import { efficiencySchema } from '../schema';
import { Nullable } from '../../../../global/types';
import { getDataPoints } from '../../../../utils/utils';
import { efficiency } from '../../../../utils/equation';
import { SchemaValidator } from '../../../../global/SchemaValidator';

const DEFAULT_LOAD_MIN = 1;
const DEFAULT_LOAD_MAX = 100;

export const getEfficiencyModel = (obj: unknown): EfficiencyModel => {
  const schemaValidator = new SchemaValidator(efficiencySchema);
  if (!schemaValidator.validate(obj)) {
    throw schemaValidator.errors;
  }

  const efficiencyModelWithExtraProps = obj as EfficiencyModel & {
    xaxisMin?: Nullable<number>;
    xaxisMax?: Nullable<number>;
    yaxisMin?: Nullable<number>;
    yaxisMax?: Nullable<number>;
    xaxisTitle: string;
    yaxisTitle: string;
    curveTestCondition?: string;
    printLabel: string;
  };

  const {
    xaxisMin,
    xaxisMax,
    yaxisMin,
    yaxisMax,
    xaxisTitle,
    yaxisTitle,
    curveTestCondition,
    printLabel,
    ...efficiencyModel
  } = efficiencyModelWithExtraProps;

  efficiencyModel.translations = {
    xAxisLabel: xaxisTitle,
    yAxisLabel: yaxisTitle,
    info: curveTestCondition,
    printLabel,
  };
  efficiencyModel.axis = {
    x: {
      min: xaxisMin,
      max: xaxisMax,
    },
    y: {
      min: yaxisMin,
      max: yaxisMax,
    },
  };

  if (
    efficiencyModel.curveLoadMin <= 0
    || efficiencyModel.curveLoadMin > DEFAULT_LOAD_MAX
  ) {
    efficiencyModel.curveLoadMin = DEFAULT_LOAD_MIN;
  }

  if (
    efficiencyModel.curveLoadMax <= 0
    || efficiencyModel.curveLoadMax > DEFAULT_LOAD_MAX
  ) {
    efficiencyModel.curveLoadMax = DEFAULT_LOAD_MAX;
  }

  // throw if efficiency equation returns efficiency outside of (0, 100] %
  const labels = getDataPoints(
    efficiencyModel.curveLoadMin,
    efficiencyModel.curveLoadMax,
    Math.floor(efficiencyModel.curveLoadMax - efficiencyModel.curveLoadMin),
  );

  const efficiencyFunction = efficiency(efficiencyModel);
  if (
    labels.some((x) => {
      const efficiency = efficiencyFunction(x);
      return !(efficiency > 0 && efficiency <= 100);
    })
  ) {
    throw new Error('Efficiency coefficients produced negative efficiency');
  }

  // adjust yAxisMin when Efficiency is smaller than yAxisMin
  // https://schneider.atlassian.net/browse/SPC-1518
  const efficiencyStart = efficiencyFunction(labels[0]);
  if (efficiencyStart < efficiencyModel.axis.y.min) {
    const wholeEfficiency = Math.floor(efficiencyStart);
    const roundedEfficiency = wholeEfficiency - (wholeEfficiency % 10);
    efficiencyModel.axis.y.min = roundedEfficiency;
  }

  return efficiencyModel;
};
